import { Button, Flex, Table } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { MenuProps } from 'antd/lib'
import dayjs from 'dayjs'
import { useState } from 'react'
import { useParams } from 'react-router-dom'

import { usePersonRiskGroupServiceGetPersonRiskGroupsEndpoint } from 'src/api/queries'
import { GetPersonRiskGroupsResponseItem } from 'src/api/requests'
import { TableActionButtons, Text } from 'src/components'
import { useTablePagination } from 'src/hooks'
import { dayMonthYearFormatWithDot } from 'src/libs'
import { useLocales } from 'src/locales'

import { AddToRiskGroupModal, FinalizeRiskGroupModal } from '../components'

import { useStyles } from './styles'

export const PersonsRiskGroupsPage = () => {
  const { id } = useParams()
  const { styles } = useStyles()

  const { t, currentLang } = useLocales()

  const [isOpen, setIsOpen] = useState(false)
  const [isOpenFinalize, setIsOpenFinalize] = useState(false)

  const pagination = useTablePagination()

  const queryParams = {
    pageNumber: pagination.current,
    pageSize: pagination.pageSize,
    personId: String(id)
  }

  const { data, isLoading } =
    usePersonRiskGroupServiceGetPersonRiskGroupsEndpoint(queryParams)

  const onToggle = () => setIsOpen((prev) => !prev)

  const onToggleFinalize = () => setIsOpenFinalize((prev) => !prev)

  const getActionItems = (endDate?: string | null): MenuProps['items'] => [
    ...(!endDate
      ? [
          {
            key: 'finalize',
            label: t('risk_groups.finalize'),
            onClick: onToggleFinalize
          }
        ]
      : [])
  ]

  const columns: ColumnsType<GetPersonRiskGroupsResponseItem> = [
    {
      key: 'start date',
      title: t('risk_groups.start_date'),
      render: (_, { startDate }) =>
        dayjs(startDate).format(dayMonthYearFormatWithDot)
    },
    {
      key: 'end date',
      title: t('risk_groups.end_date'),
      render: (_, { endDate }) =>
        endDate ? dayjs(endDate).format(dayMonthYearFormatWithDot) : '-'
    },
    {
      key: 'risk group',
      title: t('risk_groups.risk_group'),
      render: (_, { riskGroup }) => riskGroup?.translation?.[currentLang?.value]
    },
    {
      key: 'exclusion reason',
      title: t('risk_groups.exclusion_reason'),
      render: (_, { exclusionReason }) =>
        exclusionReason?.translation?.[currentLang?.value] || '-'
    },
    {
      key: 'removed at',
      title: t('risk_groups.removed_at'),
      render: (_, { removedAt }) =>
        removedAt
          ? dayjs(removedAt).format(`${dayMonthYearFormatWithDot} HH:mm`)
          : '-'
    },
    {
      key: 'removed by',
      title: t('risk_groups.removed_by'),
      render: (_, { removedBy }) =>
        removedBy ? `${removedBy.firstName} ${removedBy.lastName}` : '-'
    },
    {
      key: 'actions',
      render: (_, { endDate }) => (
        <TableActionButtons items={getActionItems(endDate)} />
      )
    }
  ]

  return (
    <Flex vertical gap={16}>
      <Flex justify="space-between">
        <Text className={styles.text}>{t('persons.person_risk-groups')}</Text>

        <Button type="primary" onClick={onToggle}>
          {t('risk_groups.add_to_group')}
        </Button>
      </Flex>

      <Table
        bordered
        dataSource={data?.data}
        columns={columns}
        loading={isLoading}
        rowKey="id"
        scroll={{ x: 'max-content' }}
        pagination={{ ...pagination, total: data?.totalCount }}
      />

      {isOpen && <AddToRiskGroupModal onCancel={onToggle} />}

      {isOpenFinalize && <FinalizeRiskGroupModal onCancel={onToggleFinalize} />}
    </Flex>
  )
}
